import { Injectable } from '@angular/core';
import deCH from '@assets/i18n/de-CH.json';
import enUS from '@assets/i18n/en-US.json';
import frCH from '@assets/i18n/fr-CH.json';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { StorageEnum } from '@shared/enums/storage.enum';
import {
  LanguageInterface,
  TranslateServiceLanguage,
} from '@shared/interfaces/language.interface';
import { TenantLanguageSource } from '@shared/interfaces/tenant-language.interface';
import { StringUtils } from '@shared/utils/string-utils';
import { TimeUtils } from '@shared/utils/time-utils';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { TenantLanguageService } from './../http/tenant-language.service';

/**
 * Running `npm translations:extract` will include the given string by using this.
 * @param text The string to extract for translation.
 * @return The same string.
 */
export function extract(text: string): string {
  return text;
}

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  defaultLanguage!: string;
  supportedLanguages!: LanguageInterface[];

  public supportedLanguages$ = new BehaviorSubject<LanguageInterface[]>([]);
  public language$ = new BehaviorSubject<LanguageInterface>(null);

  private languageChangeSubscription!: Subscription;
  constructor(
    private readonly cookieService: CookieService,
    private readonly translateService: TranslateService,
    private readonly tenantLanguageService: TenantLanguageService
  ) {
    translateService.setTranslation(TranslateServiceLanguage.en, enUS);
    translateService.setTranslation(TranslateServiceLanguage.de, deCH);
    translateService.setTranslation(TranslateServiceLanguage.fr, frCH);

    console.log('language path', this.languagePath);
    if (this.languagePath === '/de_ch') {
      translateService.setFallbackLang(TranslateServiceLanguage.de);
    } else if (this.languagePath === '/en_us') {
      translateService.setFallbackLang(TranslateServiceLanguage.en);
    } else {
      translateService.setFallbackLang(TranslateServiceLanguage.fr);
    }
  }

  /**
   * Initializes i18n for the application.
   * @param preferredLocale The logged-in user's preferred locale. Only used
   * when it matches a supported language; the URL locale still wins because
   * a preferred locale is only passed in when the URL carries none.
   */
  async init(preferredLocale?: string) {
    const preferredLanguage = preferredLocale
      ? this.supportedLanguages?.find(language =>
          StringUtils.equal(language.locale, preferredLocale)
        )
      : null;

    // trigger language changed event
    this.language = preferredLanguage ?? null;
    this.languageChangeSubscription =
      this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        this.cookieService.delete(StorageEnum.locale, '/');
        this.cookieService.delete(StorageEnum.locale);
        this.setCookie(StorageEnum.locale, event.lang);
      });
  }

  private setCookie(key: string, value: string): void {
    const expiredDate = TimeUtils.getDateWithOffset(new Date(), 1);
    this.cookieService.set(key, value, expiredDate, '/');
  }

  private getBrowserLang(): string {
    const cultureLang = this.translateService.getBrowserCultureLang();
    return cultureLang.replace('-', '_');
  }

  /**
   * @param tenantLanguages languages already returned with the tenant: when present they are
   * used as they are, otherwise they are requested from `tenant-languages`.
   */
  public loadLanguageForTenant(
    preferredLocale?: string | null,
    tenantLanguages?: TenantLanguageSource[] | null
  ): Observable<any> {
    const languages$: Observable<TenantLanguageSource[]> =
      tenantLanguages?.length
        ? of(tenantLanguages)
        : this.tenantLanguageService.gets();

    return new Observable(observer => {
      languages$.subscribe({
        next: (languages: TenantLanguageSource[]) => {
          if (languages) {
            this.supportedLanguages = languages
              .filter(item => item.activated)
              .map((item: TenantLanguageSource) => {
                return {
                  name: item.name,
                  code: item.locale ? item.locale.slice(0, 2) : '',
                  locale: item.locale,
                };
              });

            const userLang = this.getBrowserLang();
            const tenantDefaultLang = languages.find(
              lang => lang.activated && lang.isDefault
            );

            const matchedLang = this.supportedLanguages.find(
              lang => lang.locale.slice(0, 2) === userLang.slice(0, 2)
            );

            this.defaultLanguage = matchedLang
              ? matchedLang.locale
              : tenantDefaultLang?.locale;

            this.supportedLanguages$.next(this.supportedLanguages);
            this.init(preferredLocale);

            observer.next(true);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        },
      });
    });
  }

  public appendTranslationResourceFromBE(translateData: object = null): void {
    if (translateData) {
      this.translateService.setTranslation(TranslateServiceLanguage.en, {
        ...enUS,
        ...translateData,
      });
      this.translateService.setTranslation(TranslateServiceLanguage.de, {
        ...deCH,
        ...translateData,
      });
      this.translateService.setTranslation(TranslateServiceLanguage.fr, {
        ...frCH,
        ...translateData,
      });
    }
  }
  /**
   * Cleans up language change subscription.
   */
  destroy(): void {
    if (this.languageChangeSubscription) {
      this.languageChangeSubscription.unsubscribe();
    }
  }

  /**
   * Forces to reload language to default one if container webapp locale does not match.
   */
  checkExternalMatchedDefaultLangAndReload(): void {
    const cookiesLang = this.cookieService.get(StorageEnum.locale) || '';
    const isNotMatchedCookies =
      cookiesLang !== this.translateService.getCurrentLang();
    const isNotSupported = !this.supportedLanguages?.some(
      lang => lang.locale === cookiesLang
    );
    if (isNotMatchedCookies && isNotSupported) {
      this.setCookie(StorageEnum.locale, this.defaultLanguage);
    }
  }

  /**
   * Set the current language.
   * @param language The IETF language code to set.
   */
  set language(language: LanguageInterface | null) {
    let locale =
      language?.locale ||
      StringUtils.getLocaleFromUrl() ||
      this.cookieService.get(StorageEnum.locale) ||
      this.getBrowserLang();

    if (this.supportedLanguages) {
      language = this.supportedLanguages.find(l =>
        StringUtils.equal(l.locale, locale)
      );
    }

    if (locale && language === undefined) {
      locale = this.defaultLanguage;
    } else {
      locale = language.locale;
    }

    this.setCookie(StorageEnum.locale, locale);
    this.translateService.use(locale);
  }

  /**
   * Get the current language.
   * @return The current language code.
   */
  get language(): LanguageInterface {
    if (this.supportedLanguages) {
      let currentLocale = this.translateService.getCurrentLang();

      currentLocale ??= this.defaultLanguage;

      let matchedSupported = this.supportedLanguages.find(
        l => l.locale === currentLocale
      );
      if (!matchedSupported) {
        // match code only
        matchedSupported = this.supportedLanguages.find(
          lang => lang.locale.slice(0, 2) === currentLocale.slice(0, 2)
        );
      }
      return matchedSupported;
    } else {
      const browserLang = this.getBrowserLang();
      const code = browserLang.slice(0, 2);
      return { name: code, code, locale: browserLang };
    }
  }

  get languagePath(): string {
    const lang =
      StringUtils.getLocaleFromUrl() ||
      this.language?.locale ||
      TranslateServiceLanguage.en.toLocaleLowerCase();

    return '/' + lang.toLowerCase();
  }
}
